import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { T } from '../../theme/tokens';

// Exec ("אופרטיבי") dashboards — flat list, one click from the side nav.
// hub keeps `end` so it doesn't stay highlighted on its sub-routes.
//
// What appears here is decided by the backend, from the user's role plus their
// per-dashboard grants (Ohad 2026-07-26). The old devOnly/adminOnly flags are gone:
// hiding a prototype is now "grant it to nobody", not a line of code. `id` is what
// ties each item to its entry in the dashboard map.
const execItems: { id: string; label: string; path: string; icon: string; end?: boolean }[] = [
  { id: 'ask', label: 'דייויד', path: '/exec/ask', icon: '🧠', end: true },
  { id: 'exec', label: 'דשבורד הנהלה', path: '/exec', icon: '◊', end: true },
  { id: 'financial', label: 'תחזית עסקית', path: '/exec/financial', icon: '₪' },
  { id: 'staff', label: 'כוח אדם', path: '/exec/staff', icon: '▤' },
  { id: 'meetings', label: 'פגישות, ביטולים ו-no show', path: '/exec/meetings', icon: '◷', end: true },
  { id: 'meetings_list', label: 'סייר פגישות', path: '/exec/meetings/list', icon: '▦' },
  { id: 'patients', label: 'מסע מטופל', path: '/exec/patients', icon: '□' },
  { id: 'satisfaction', label: 'שביעות רצון', path: '/exec/satisfaction', icon: '◈' },
  { id: 'wellness', label: 'וולנס — אפליקציה', path: '/exec/wellness', icon: '♡' },
  { id: 'forecast', label: 'תחזית קיבולת', path: '/exec/forecast', icon: '◉' },
  { id: 'cm_bonus', label: 'בונוס סטים — מנהלי טיפול', path: '/exec/cm-bonus', icon: '✦' },
  { id: 'salary', label: 'דשבורד תשלומים', path: '/exec/salary', icon: '₪' },
  { id: 'billing', label: 'הנהלת חשבונות', path: '/exec/billing', icon: '⚖' },
  { id: 'oncall', label: 'סידור כוננות', path: '/exec/oncall', icon: '☎' },
  { id: 'reports', label: 'מחולל דוחות', path: '/exec/reports', icon: '✉' },
];

// The original dashboard, kept separate from the exec block. Grant-driven like the
// rest since 2026-07-26 — it used to show for everyone regardless of access.
// The old Meetings stack that sat here was deleted 2026-07-26 (dropped from nav
// 2026-07-07, fully replaced by the exec meetings drill + explorer).
const legacyItems: { id: string; label: string; path: string; icon: string; end?: boolean }[] = [
  { id: 'patient_base', label: 'מטופלים המלא', path: '/patients-full', icon: '□', end: true },
];

const adminItems = [
  { label: 'ניהול משתמשים', path: '/admin', icon: '◎' },
  { label: 'ניהול דשבורדים ונתונים', path: '/admin/data', icon: '▣' },
];

/* One row = one dashboard. On the dark ground the active row cannot rely on a tint
   the way it did on white: it gets a raised panel, white bold text AND a blue bar on
   the leading edge, so "you are here" survives a dim screen and a colour-blind eye.
   Inactive rows stay quiet — a nav where every row shouts is a nav nobody scans. */
function NavItem({ to, end, icon, label }: { to: string; end?: boolean; icon: string; label: string }) {
  return (
    <NavLink
      to={to}
      end={end}
      // Hover lives in CSS (`.tz-nav:hover` in index.css) rather than in mouse
      // handlers: NavLink rewrites this style object on every render, so anything
      // JavaScript paints onto the element is wiped the next time the route changes.
      className="tz-nav"
      style={({ isActive }) => ({
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px', borderRadius: T.radiusSm,
        fontSize: 13, lineHeight: 1.35,
        textDecoration: 'none',
        fontWeight: isActive ? 700 : 400,
        color: isActive ? '#FFFFFF' : T.onDarkSoft,
        background: isActive ? T.sidebarRaised : 'transparent',
        transition: 'background .12s ease, color .12s ease',
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span aria-hidden style={{
              position: 'absolute', insetInlineStart: 0, top: 7, bottom: 7,
              width: 3, borderRadius: 3, background: T.blue,
            }} />
          )}
          <span aria-hidden style={{ fontSize: 13, width: 16, textAlign: 'center', flexShrink: 0, opacity: isActive ? 1 : .75 }}>
            {icon}
          </span>
          <span style={{ minWidth: 0 }}>{label}</span>
        </>
      )}
    </NavLink>
  );
}

function Divider() {
  return <div style={{ height: 1, background: 'rgba(255,255,255,.07)', margin: '10px 4px' }} />;
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { dashboards, panels, loading } = usePermissions();

  // Follow the user's own card order from the hub (drag-to-reorder, 2026-08-15).
  // `panels` arrives already sorted by it from the backend, so ranking by its
  // index keeps the nav and the front door telling the same story.
  const rank: Record<string, number> = {};
  panels.forEach((p, i) => { rank[p.id] = i; });
  const byRank = (a: { id: string }, b: { id: string }) =>
    (rank[a.id] ?? 99) - (rank[b.id] ?? 99);

  const visible = execItems.filter((item) => !!dashboards[item.id]).sort(byRank);
  const visibleLegacy = legacyItems.filter((item) => !!dashboards[item.id]);

  /* The nav sits on the RIGHT. The reference mockup draws it on the left, and it was
     built that way first — but the whole product is Hebrew, and in Hebrew the side
     the eye starts from is the right (Ohad, 18/10). Only the SIDE came back; the
     dark navy panel from the design direction stays.

     `order: 2` is what moves it, rather than editing the ~21 pages that all render
     <Sidebar /> before their content. Those outer containers are LTR, so the last
     flex item is the rightmost one.

     `dir="rtl"` on the panel puts the icon on the outer (right) edge of each row and
     makes `insetInlineStart` resolve to that same edge, so the active row's accent
     bar lands against the screen edge rather than floating inside the text. */
  return (
    <aside
      dir="rtl"
      style={{
        order: 2,
        width: 228, flexShrink: 0, background: T.sidebar,
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Wordmark. Typographic rather than an image: there is no Taliaz logo file in
          this repo, and a redrawn approximation of a real brand mark is worse than
          clean type. */}
      <div style={{ padding: '18px 16px 16px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: '#FFFFFF', letterSpacing: '-.02em', lineHeight: 1.1 }}>
          Taliaz
        </div>
        <div style={{ fontSize: 11, color: T.onDarkSoft, letterSpacing: '.14em', marginTop: 3 }}>
          HEALTH
        </div>
      </div>

      {/* Render nothing until we know — rows that appear and vanish read as a bug,
          and a half-drawn nav is worse than a blank one for the second it takes. */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!loading && (
          <>
            {/* Back to the hub. Always present — it is the one page every signed-in
                person may open, whatever they were granted. */}
            <NavItem to="/" end icon="⌂" label="הדשבורדים שלי" />
            <Divider />
            {visible.map((item) => (
              <NavItem key={item.path} to={item.path} end={item.end} icon={item.icon} label={item.label} />
            ))}
            {!!visibleLegacy.length && <Divider />}
            {visibleLegacy.map((item) => (
              <NavItem key={item.path} to={item.path} end={item.end} icon={item.icon} label={item.label} />
            ))}
            {user?.role === 'admin' && (
              <>
                <Divider />
                {adminItems.map((item) => (
                  <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label} />
                ))}
              </>
            )}
            {/* Training platform — a real in-app route (/training) guarded by the
                `training` grant; the page fronts the local prototype servers. */}
            {!!dashboards['training'] && (
              <NavItem to="/training" icon="🎓" label="הכשרה" />
            )}
          </>
        )}
      </nav>

      <div style={{ padding: '12px 16px 14px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
        <div style={{ fontSize: 11, color: T.onDarkSoft, marginBottom: 7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user?.email}
        </div>
        <button
          onClick={logout}
          style={{
            fontSize: 12, color: T.onDark, background: 'transparent',
            border: '1px solid rgba(255,255,255,.14)', borderRadius: 6,
            padding: '5px 12px', cursor: 'pointer',
          }}
        >
          התנתקות
        </button>
        {/* Which build is actually being served. The pipeline injects the build number
            at compile time; a stale CDN copy or a deploy that never landed shows the
            OLD number here, which is otherwise invisible from the browser. */}
        {process.env.REACT_APP_BUILD && (
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginTop: 9 }} title="מספר הבנייה שמוגשת כרגע">
            build {process.env.REACT_APP_BUILD}
          </div>
        )}
      </div>
    </aside>
  );
}
